import { isNil } from "./number";

/**
 * Capacity formatting utilities for batteries and energy storage
 *
 * Domain-specific formatters for energy capacity (kWh) and power ratings (kW).
 * Auto-scales to appropriate units (MWh, MW for large values).
 */

/**
 * Format battery capacity (kWh)
 *
 * @param value - Capacity in kWh
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted capacity string or null
 *
 * @example
 * ```typescript
 * formatCapacity(13.5); // "13.5 kWh"
 * formatCapacity(1500); // "1.5 MWh"
 * formatCapacity(null); // null
 * ```
 */
export function formatCapacity(value: number | null | undefined, decimals = 1): string | null {
  if (isNil(value)) return null;

  const absValue = Math.abs(value);

  // MWh for large capacities
  if (absValue >= 1000) {
    return `${(value / 1000).toFixed(decimals)} MWh`;
  }

  // kWh for normal capacities
  return `${value.toFixed(decimals)} kWh`;
}

/**
 * Format power rating (kW)
 *
 * @param value - Power rating in kW
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted power rating string or null
 *
 * @example
 * ```typescript
 * formatPowerRating(5.2); // "5.2 kW"
 * formatPowerRating(2500); // "2.5 MW"
 * formatPowerRating(null); // null
 * ```
 */
export function formatPowerRating(value: number | null | undefined, decimals = 1): string | null {
  if (isNil(value)) return null;

  const absValue = Math.abs(value);

  // MW for large ratings
  if (absValue >= 1000) {
    return `${(value / 1000).toFixed(decimals)} MW`;
  }

  // kW for normal ratings
  return `${value.toFixed(decimals)} kW`;
}
